import React, { useEffect, useState, useCallback } from "react";
import { ref, get, child } from "firebase/database";
import { database } from "../../firebase";
import TopBar from "../Sidebar/SidebarUser";
import {
  Box,
  Typography,
  CircularProgress,
  List,
  ListItemButton,
  Paper,
  Fade,
  Chip,
  Divider,
  Container
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import MeetingRoomOutlinedIcon from "@mui/icons-material/MeetingRoomOutlined";
import PaymentOutlinedIcon from "@mui/icons-material/PaymentOutlined";

const ActivityLog = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("currentUser"));
    if (!storedUser) {
      setError("User not logged in.");
      setLoading(false);
      return;
    }
    setCurrentUser(storedUser);
  }, []);

  useEffect(() => {
    if (!currentUser) return;
    const fetchActivityLogs = async () => {
      try {
        const dbRef = ref(database);
        const snapshot = await get(child(dbRef, "activityLog"));
        if (snapshot.exists()) {
          const logData = snapshot.val();
          const activityArray = [];

          if (Array.isArray(logData)) {
            logData.forEach((group) => {
              if (group) {
                Object.entries(group).forEach(([activityId, activity]) => {
                  activityArray.push({ id: activityId, ...activity });
                });
              }
            });
          } else {
            Object.entries(logData).forEach(([groupKey, groupValue]) => {
              if (groupValue) {
                Object.entries(groupValue).forEach(([activityId, activity]) => {
                  activityArray.push({ id: activityId, ...activity });
                });
              }
            });
          }

          const filteredActivities = activityArray.filter(
            (activity) =>
              activity.assignedTo === currentUser.email ||
              activity.fingerprintID === currentUser.fingerprintID
          );

          filteredActivities.sort(
            (a, b) => new Date(b.rentTime) - new Date(a.rentTime)
          );

          setActivities(filteredActivities);
        } else {
          setError("No activity logs found.");
        }
      } catch (err) {
        console.error(err);
        setError("Failed to fetch activity logs.");
      } finally {
        setLoading(false);
      }
    };

    fetchActivityLogs();
  }, [currentUser]);

  const toggleExpand = useCallback(
    (id) => {
      setExpandedId((prev) => (prev === id ? null : id));
    },
    [setExpandedId]
  );

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });

  const formatTime = (dateStr) =>
    new Date(dateStr).toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    });

  const ActivityCard = React.memo(({ activity, isExpanded, onToggle }) => {
    const returnDate = activity.returnTime ? new Date(activity.returnTime) : null;
    const formattedReturnDate = returnDate
      ? returnDate.toLocaleDateString(undefined, {
          weekday: "short",
          month: "short",
          day: "numeric",
        })
      : "No return";
    
    const rentDate = activity.rentTime ? new Date(activity.rentTime) : null;
    const formattedRentTime = rentDate 
      ? rentDate.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
      : "-";

    return (
      <Paper
        elevation={isExpanded ? 4 : 1}
        sx={{
          mb: 2,
          borderRadius: 3,
          overflow: "hidden",
          transition: "all 0.3s ease",
          border: isExpanded ? "1px solid #3b99a1" : "1px solid rgba(59,153,161,0.1)",
        }}
      >
        <ListItemButton
          onClick={() => onToggle(activity.id)}
          sx={{
            p: 2,
            bgcolor: isExpanded ? "rgba(59,153,161,0.08)" : "#fff",
            color: "#054d56",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            cursor: "pointer",
            transition: "background-color 0.2s ease",
            "&:hover": {
              bgcolor: isExpanded ? "rgba(59,153,161,0.12)" : "rgba(59,153,161,0.04)",
            },
          }}
          aria-expanded={isExpanded}
          aria-controls={`activity-details-${activity.id}`}
          role="button"
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <CalendarTodayOutlinedIcon sx={{ color: "#3b99a1" }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {formattedReturnDate}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Rented at {formattedRentTime}
              </Typography>
            </Box>
          </Box>
          
          <Chip 
            icon={<MeetingRoomOutlinedIcon />} 
            label={`Locker ${activity.locker_number || "-"}`}
            size="small"
            sx={{ 
              bgcolor: "rgba(59,153,161,0.1)", 
              color: "#3b99a1",
              mr: 2,
              fontWeight: 500,
              display: { xs: 'none', sm: 'flex' }
            }}
          />
          
          {isExpanded ? (
            <KeyboardArrowUpIcon sx={{ color: "#3b99a1" }} />
          ) : (
            <KeyboardArrowDownIcon sx={{ color: "#3b99a1" }} />
          )}
        </ListItemButton>

        {isExpanded && (
          <Fade in={isExpanded} timeout={400}>
            <Box>
              <ActivityDetails
                activity={activity}
                id={`activity-details-${activity.id}`}
              />
            </Box>
          </Fade>
        )}
      </Paper>
    );
  });

  const ActivityDetails = React.memo(({ activity, id }) => {
    const rentDate = activity.rentTime ? new Date(activity.rentTime) : null;
    const returnDate = activity.returnTime ? new Date(activity.returnTime) : null;

    return (
      <Box
        id={id}
        sx={{
          p: 3,
          bgcolor: "#fff",
          borderTop: "1px solid rgba(59,153,161,0.1)",
        }}
        role="region"
        aria-label="Activity details"
      >
        <Typography variant="h6" sx={{ mb: 3, color: "#054d56", fontWeight: 700 }}>
          Activity Details
        </Typography>
        
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <TimelineEvent
            label="Rent Time"
            date={rentDate ? formatDate(rentDate) : "-"}
            time={rentDate ? formatTime(rentDate) : "-"}
            icon={<CalendarTodayOutlinedIcon sx={{ color: "#3b99a1" }} />}
          />
          
          <Divider sx={{ borderStyle: "dashed" }} />
          
          <TimelineEvent
            label="Return Time"
            date={returnDate ? formatDate(returnDate) : "Not returned yet"}
            time={returnDate ? formatTime(returnDate) : "-"}
            icon={<AccessTimeOutlinedIcon sx={{ color: "#3b99a1" }} />}
          />
          
          <Divider />
          
          <Box sx={{ display: "flex", justifyContent: "space-between", pt: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <MeetingRoomOutlinedIcon sx={{ color: "#3b99a1" }} />
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                Locker Number
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ fontWeight: 700, color: "#054d56" }}>
              {activity.locker_number || "-"}
            </Typography>
          </Box>
          
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <PaymentOutlinedIcon sx={{ color: "#3b99a1" }} />
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                Session Payment
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ fontWeight: 700, color: "#054d56" }}>
              ₱{activity.sessionPayment ? parseFloat(activity.sessionPayment).toFixed(2) : "0.00"}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  });

  const TimelineEvent = React.memo(({ label, date, time, icon }) => (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
      {icon}
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#054d56" }}>
          {label}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, mt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            {date}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, color: "#3b99a1" }}>
            {time}
          </Typography>
        </Box>
      </Box>
    </Box>
  ));

  if (loading) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          bgcolor: "#e6f2f4",
        }}
        aria-busy="true"
        aria-live="polite"
      >
        <CircularProgress sx={{ color: "#3b99a1" }} />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: "#e6f2f4", minHeight: "100vh" }}>
      <TopBar />
      
      <Container maxWidth="md" sx={{ pt: 10, pb: 8 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: "#3b99a1",
            mb: 1,
            textAlign: "left",
          }}
        >
          Activity
        </Typography>

        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: "#054d56",
            mb: 1,
            textAlign: "left",
          }}
        >
          Recent
        </Typography>

        {error ? (
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              borderRadius: 3, 
              bgcolor: "rgba(211, 47, 47, 0.05)", 
              border: "1px solid rgba(211, 47, 47, 0.2)" 
            }}
          >
            <Typography
              role="alert"
              color="error"
              align="center"
              sx={{ fontWeight: 500 }}
            >
              {error}
            </Typography>
          </Paper>
        ) : activities.length === 0 ? (
          <Paper 
            elevation={0} 
            sx={{ 
              p: 4, 
              borderRadius: 3,
              bgcolor: "#fff",
              border: "1px dashed rgba(59, 153, 161, 0.3)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2
            }}
          >
            <CalendarTodayOutlinedIcon sx={{ fontSize: 48, color: "rgba(59, 153, 161, 0.5)" }} />
            <Typography
              align="center"
              sx={{ color: "#054d56", fontWeight: 500 }}
            >
              No activity logs found for your account.
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary" 
              align="center"
            >
              Your locker usage history will appear here once you start using our service.
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ mt: 2 }}>
            <List disablePadding>
              {activities.map((activity) => (
                <ActivityCard
                  key={activity.id}
                  activity={activity}
                  isExpanded={expandedId === activity.id}
                  onToggle={toggleExpand}
                />
              ))}
            </List>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default ActivityLog;
